import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { dateTime, humanise, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Badge, Button, Notice, SearchInput, Select, useToast } from '../components/ui';

export interface LoginRow {
  id: string;
  identifier: string;
  method: 'OTP' | 'PASSWORD' | 'PARTNER';
  status: 'SUCCESS' | 'FAILED';
  failureReason: string | null;
  roles: string[];
  ip: string | null;
  deviceType: string | null;
  os: string | null;
  client: string | null;
  sessionId: string | null;
  createdAt: string;
  lastActiveAt: string | null;
  logoutAt: string | null;
  logoutReason: string | null;
  durationSeconds: number | null;
  isActive: boolean;
  user: { id: string; displayName: string | null; mobile: string; isBlocked: boolean } | null;
}

const METHOD_LABEL = { OTP: 'Mobile + OTP', PASSWORD: 'Admin password', PARTNER: 'Partner login' } as const;

const ENDED_LABEL: Record<string, string> = {
  USER: 'Signed out',
  EVERYWHERE: 'Signed out everywhere',
  ADMIN: 'Ended by an admin',
  BLOCKED: 'Account blocked',
  LOCKED: 'Account locked',
  REUSE: 'Token reuse detected',
};

export function duration(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86_400).toFixed(1)} days`;
}

/** The device, as far as its User-Agent says. A label, not a fact. */
export function deviceLabel(r: Pick<LoginRow, 'deviceType' | 'os' | 'client'>): string {
  return [r.client, r.os].filter(Boolean).join(' · ') || humanise(r.deviceType ?? 'unknown');
}

export function LoginHistoryPage() {
  const { get, set } = useListParams();
  const toast = useToast();
  const qc = useQueryClient();
  const search = get('search');
  const status = get('status');
  const method = get('method');
  const active = get('active');

  const end = useMutation({
    mutationFn: (sessionId: string) => api.post(`/admin/sessions/${sessionId}/revoke`, {}),
    onSuccess: () => {
      toast('Session ended. It is refused from its next request.', 'success');
      qc.invalidateQueries({ queryKey: ['login-records'] });
      qc.invalidateQueries({ queryKey: ['security-summary'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<LoginRow>> = [
    { key: 'when', header: 'When', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    {
      key: 'who',
      header: 'Who',
      render: (r) => (
        <div>
          <div className="cell-strong">
            {r.user ? (
              <Link to={`/users/${r.user.id}`}>{r.user.displayName ?? fmtMobile(r.user.mobile)}</Link>
            ) : (
              <span className="cell-muted">No such account</span>
            )}
          </div>
          <div className="cell-mono">{r.identifier}</div>
        </div>
      ),
    },
    {
      key: 'result',
      header: 'Result',
      render: (r) =>
        r.status === 'FAILED' ? (
          <div>
            <Badge tone="red">Failed</Badge>
            <div className="cell-muted">{r.failureReason ?? ''}</div>
          </div>
        ) : r.isActive ? (
          <Badge tone="green" dot>
            Signed in
          </Badge>
        ) : (
          <div>
            <Badge tone="neutral">Ended</Badge>
            <div className="cell-muted">
              {r.logoutReason ? (ENDED_LABEL[r.logoutReason] ?? humanise(r.logoutReason)) : 'Expired'}
            </div>
          </div>
        ),
    },
    {
      key: 'how',
      header: 'How',
      render: (r) => (
        <div>
          <div>{METHOD_LABEL[r.method]}</div>
          <div className="cell-muted">{r.roles.map(humanise).join(', ')}</div>
        </div>
      ),
    },
    {
      key: 'device',
      header: 'Device',
      render: (r) => (
        <div>
          <div>{deviceLabel(r)}</div>
          <div className="cell-mono">{r.ip ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'session',
      header: 'Session',
      render: (r) =>
        r.status === 'FAILED' ? (
          <span className="cell-muted">—</span>
        ) : (
          <div>
            <div>{duration(r.durationSeconds)}</div>
            <div className="cell-muted">
              {r.logoutAt
                ? `ended ${dateTime(r.logoutAt)}`
                : r.lastActiveAt
                  ? `last active ${dateTime(r.lastActiveAt)}`
                  : ''}
            </div>
          </div>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.isActive && r.sessionId ? (
          <div className="cell-actions">
            <Button
              size="sm"
              variant="danger"
              loading={end.isPending && end.variables === r.sessionId}
              onClick={() => end.mutate(r.sessionId as string)}
            >
              End session
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="stack">
      <Notice tone="info" title="Every sign-in attempt is kept">
        Successes and refusals, by mobile and OTP, admin password or partner login. A success is also the record of the
        session it started. The device is what the request said about itself, so read it as a label, not as proof.
      </Notice>

      <DataTable<LoginRow>
        queryKey={['login-records', search, status, method, active]}
        path="/admin/login-records"
        params={{ search, status, method, active }}
        columns={columns}
        emptyIcon="⏻"
        emptyTitle="No sign-ins match"
        emptyBody="Try clearing the filters."
        toolbar={
          <>
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Mobile, email, name or IP…" />
            <Select
              value={active === 'true' ? 'ACTIVE' : status}
              placeholder="All results"
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'ACTIVE') {
                  set('status', '');
                  set('active', 'true');
                } else {
                  set('active', '');
                  set('status', v);
                }
              }}
              options={[
                { value: 'ACTIVE', label: 'Signed in now' },
                { value: 'SUCCESS', label: 'Successful' },
                { value: 'FAILED', label: 'Failed' },
              ]}
            />
            <Select
              value={method}
              placeholder="Any method"
              onChange={(e) => set('method', e.target.value)}
              options={Object.entries(METHOD_LABEL).map(([value, label]) => ({ value, label }))}
            />
          </>
        }
      />
    </div>
  );
}
